import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

export default function NewsletterConfirm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error" | "already">("loading");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    const confirmSubscription = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Invalid confirmation link");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("confirm-newsletter", {
          body: { token },
        });

        if (error) throw error;

        if (data.success) {
          setEmail(data.email);
          if (data.alreadyConfirmed) {
            setStatus("already");
            setMessage(data.message);
          } else {
            setStatus("success");
            setMessage(data.message);
          }
        } else {
          setStatus("error");
          setMessage(data.error || "Failed to confirm subscription");
        }
      } catch (error: any) {
        console.error("Error confirming newsletter:", error);
        setStatus("error");
        setMessage(error.message || "Failed to confirm subscription");
      }
    };

    confirmSubscription();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-muted/20">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center space-y-4">
          {status === "loading" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}
          {status === "success" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-success" />
            </div>
          )}
          {status === "already" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-primary" />
            </div>
          )}
          {status === "error" && (
            <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
          )}
          
          <CardTitle className="text-2xl">
            {status === "loading" && "Confirming Your Subscription..."}
            {status === "success" && "Subscription Confirmed!"}
            {status === "already" && "Already Subscribed"}
            {status === "error" && "Confirmation Failed"}
          </CardTitle>
          <CardDescription>
            {message}
          </CardDescription>
          {email && (
            <p className="text-sm text-muted-foreground">
              Email: <span className="font-medium text-foreground">{email}</span>
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {status === "success" && (
            <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
              <h3 className="font-semibold text-sm">What's Next?</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• You'll receive our latest updates and offers</li>
                <li>• Check your inbox for a welcome email</li>
                <li>• You can unsubscribe anytime</li>
              </ul>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button asChild className="flex-1">
              <Link to="/">
                Back to Home
              </Link>
            </Button>
            <Button asChild variant="outline" className="flex-1">
              <Link to="/shop">
                Browse Products
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
